/**
 * Authored musical content: note-string patterns for the menu theme, the
 * in-game ambient loop (+ its tension layer), and the short stingers.
 *
 * Grid: 16 steps/bar (sixteenth notes). '-' = rest, '.' = hold the previous
 * note through this step (see notes.ts). Bars are written as flat 16-token
 * arrays and concatenated into phrases, so the musical form (A/A'/B/A,
 * 4-bar loops, etc.) is visible in the code that assembles them.
 */

import { bar, restBar } from './notes';

// ---------------------------------------------------------------------------
// Menu theme — calm town-theme chiptune, A minor / C major, 76 BPM.
// ---------------------------------------------------------------------------

export const MENU_BPM = 76;
export const MENU_STEPS_PER_BAR = 16;

type ChordName = 'Am' | 'F' | 'C' | 'G' | 'Dm' | 'E';

// One whole bar of quiet triangle bass per chord: root held 8 steps, then
// the fifth held 8 steps — a gentle root-fifth "walk" instead of a static
// drone, without ever leaving the chord.
const BASS_BAR: Record<ChordName, string[]> = {
  Am: bar('A2 . . . . . . . E3 . . . . . . .'),
  F: bar('F2 . . . . . . . C3 . . . . . . .'),
  C: bar('C3 . . . . . . . G3 . . . . . . .'),
  G: bar('G2 . . . . . . . D3 . . . . . . .'),
  Dm: bar('D3 . . . . . . . A3 . . . . . . .'),
  E: bar('E3 . . . . . . . B3 . . . . . . .'),
};

function bassFromChords(chords: ChordName[]): string[] {
  return chords.flatMap((c) => BASS_BAR[c]);
}

const MENU_CHORDS_A: ChordName[] = ['Am', 'F', 'C', 'G', 'Am', 'F', 'Dm', 'E'];
const MENU_CHORDS_B: ChordName[] = ['F', 'C', 'G', 'Am', 'F', 'C', 'Dm', 'E'];

// Sparse soft-pulse lead motifs (all diatonic to A minor / C major, so they
// sit consonantly under any bar regardless of the exact chord underneath).
// Each is a full 16-step bar with a short phrase up front and a long tail
// of rests — "lots of rests" per the brief.
const leadM1 = bar('- - - - C5 . - - B4 . - - A4 . . -');
const leadM2 = bar('- - - - A4 . - - G4 . - - F4 . . -');
const leadM3 = bar('- - - - E5 . - - D5 . - - C5 . . -');
const leadM4 = bar('- - - - D5 . C5 . B4 . - - - - - -');
const leadM5 = bar('- - E5 . - - D5 . - - C5 . - - - -');
const leadM6 = bar('- - - - G4 . A4 . B4 . - - C5 . - -');
const leadM7 = bar('- - - - F4 . - - E4 . - - D4 . . -');
const leadM8 = bar('- - A4 . - - G4 . - - F4 . - - - -');
const rest16 = restBar(16);

// Section A: motif on odd bars, a full bar of rest to breathe on even bars.
const MENU_LEAD_A = [leadM1, rest16, leadM2, rest16, leadM3, rest16, leadM4, rest16].flat();
// Section A': same chords, motifs re-ordered so the repeat isn't identical.
const MENU_LEAD_A2 = [leadM2, rest16, leadM1, rest16, leadM4, rest16, leadM3, rest16].flat();
// Section B: new chords, new motifs.
const MENU_LEAD_B = [leadM5, rest16, leadM6, rest16, leadM7, rest16, leadM8, rest16].flat();

/** Form: A A' B A — a musically-alive 32-bar loop built from a handful of
 * hand-written 8-bar phrases. */
export const MENU_BASS: string[] = [
  ...bassFromChords(MENU_CHORDS_A),
  ...bassFromChords(MENU_CHORDS_A),
  ...bassFromChords(MENU_CHORDS_B),
  ...bassFromChords(MENU_CHORDS_A),
];
export const MENU_LEAD: string[] = [...MENU_LEAD_A, ...MENU_LEAD_A2, ...MENU_LEAD_B, ...MENU_LEAD_A];

// ---------------------------------------------------------------------------
// In-game ambient loop — D dorian, low-key, 82 BPM.
//
// The underlying harmony is a short 4-bar loop (Dm - C - Dm - Am), but the
// pulse-arpeggio voice rotates between 4 hand-written variants (picked
// pseudo-randomly by music.ts, never repeating the previous pick), and the
// noise-hat only plays on every other bar. That combination means a
// listener has to sit through many 4-bar cycles before hearing the exact
// same variant sequence twice — the "60-90s before repetition is
// noticeable" target — without needing a huge authored file.
// ---------------------------------------------------------------------------

export const GAME_BPM = 82;
export const GAME_STEPS_PER_BAR = 16;
export const GAME_BARS_PER_PHRASE = 4;

const GAME_BASS_BAR: Record<'Dm' | 'C' | 'Am', string[]> = {
  Dm: bar('D3 . . . . . . . A3 . . . . . . .'),
  C: bar('C3 . . . . . . . G3 . . . . . . .'),
  Am: bar('A2 . . . . . . . E3 . . . . . . .'),
};

/** Quiet triangle bassline, one 4-bar phrase, looped forever. */
export const GAME_BASS: string[] = [
  ...GAME_BASS_BAR.Dm,
  ...GAME_BASS_BAR.C,
  ...GAME_BASS_BAR.Dm,
  ...GAME_BASS_BAR.Am,
];

/** Expand 8 eighth-note tone slots (one per pair of sixteenth steps) into a
 * full 16-step bar. Keeps the arpeggio patterns readable as a plain list of
 * chord tones instead of 16 hand-counted tokens. */
function arpBar(tones8: readonly string[]): string[] {
  if (tones8.length !== 8) throw new Error(`arpBar(): expected 8 tones, got ${tones8.length}`);
  return tones8.flatMap((t) => [t, '-']);
}

function arpPhrase(dm: readonly string[], c: readonly string[], am: readonly string[]): string[] {
  return [...arpBar(dm), ...arpBar(c), ...arpBar(dm), ...arpBar(am)];
}

/** Four rotating arpeggio variants, all in a moderate register (D3-A4) —
 * deliberately no high-register lead riffs. */
export const GAME_ARP_VARIANTS: string[][] = [
  // 1: steady up-down arc
  arpPhrase(
    ['D4', 'F4', 'A4', 'F4', 'D4', '-', 'A3', '-'],
    ['C4', 'E4', 'G4', 'E4', 'C4', '-', 'G3', '-'],
    ['A3', 'C4', 'E4', 'C4', 'A3', '-', 'E3', '-'],
  ),
  // 2: broken thirds, sparser
  arpPhrase(
    ['D4', '-', 'F4', '-', 'A3', '-', '-', '-'],
    ['C4', '-', 'E4', '-', 'G3', '-', '-', '-'],
    ['A3', '-', 'C4', '-', 'E3', '-', '-', '-'],
  ),
  // 3: syncopated, leads with a rest
  arpPhrase(
    ['-', 'A4', '-', 'F4', '-', 'D4', '-', '-'],
    ['-', 'G4', '-', 'E4', '-', 'C4', '-', '-'],
    ['-', 'E4', '-', 'C4', '-', 'A3', '-', '-'],
  ),
  // 4: calmest — just a root/fifth pulse
  arpPhrase(
    ['D4', '-', '-', '-', 'A3', '-', '-', '-'],
    ['C4', '-', '-', '-', 'G3', '-', '-', '-'],
    ['A3', '-', '-', '-', 'E3', '-', '-', '-'],
  ),
];

/** Steps within a bar (16-step grid) where the sparse hat ticks, only on
 * odd-indexed bars (every other bar) per the brief. */
export const GAME_HAT_STEPS = [4, 12];

/** Bass root per bar of the 4-bar phrase, reused by the tension layer so it
 * always shares the ambient loop's harmony instead of clashing with it. */
export const GAME_BAR_ROOTS = ['D3', 'C3', 'D3', 'A2'];

// ---------------------------------------------------------------------------
// Tension layer — same key & tempo as the ambient loop; a low pulsing pulse
// voice on the current bar's root plus a faster hat. Always scheduled
// alongside the ambient loop; music.ts crossfades its bus gain in/out so it
// *layers*, never switches.
// ---------------------------------------------------------------------------

/** Steps (of 16) where the low pulse note fires — eighth notes. */
export const TENSION_PULSE_STEPS = [0, 2, 4, 6, 8, 10, 12, 14];
/** Steps (of 16) where the faster hat fires — also eighth notes, denser
 * than the ambient loop's sparse hat. */
export const TENSION_HAT_STEPS = [0, 2, 4, 6, 8, 10, 12, 14];

// ---------------------------------------------------------------------------
// Stingers — short, self-contained note-event lists (not grid patterns).
// Each event is relative to a start time; total length capped at ~2s.
// ---------------------------------------------------------------------------

export interface StingerNote {
  /** offset from stinger start, seconds */
  at: number;
  note: string;
  dur: number;
  shape: 'square' | 'triangle' | 'sawtooth';
  gain: number;
  duty?: number;
}

/** Victory fanfare: quick major ascent into a held triad, ~1.8s. */
export const STINGER_VICTORY: StingerNote[] = [
  { at: 0.0, note: 'C5', dur: 0.16, shape: 'square', duty: 0.35, gain: 0.16 },
  { at: 0.16, note: 'E5', dur: 0.16, shape: 'square', duty: 0.35, gain: 0.16 },
  { at: 0.32, note: 'G5', dur: 0.16, shape: 'square', duty: 0.35, gain: 0.16 },
  { at: 0.48, note: 'C6', dur: 0.5, shape: 'square', duty: 0.35, gain: 0.17 },
  { at: 0.48, note: 'E5', dur: 0.5, shape: 'triangle', duty: undefined, gain: 0.12 },
  { at: 0.48, note: 'G5', dur: 0.5, shape: 'triangle', duty: undefined, gain: 0.1 },
];

/** Somber defeat phrase: slow minor descent, ~1.8s. */
export const STINGER_DEFEAT: StingerNote[] = [
  { at: 0.0, note: 'A4', dur: 0.42, shape: 'triangle', gain: 0.16 },
  { at: 0.4, note: 'G4', dur: 0.42, shape: 'triangle', gain: 0.15 },
  { at: 0.8, note: 'F4', dur: 0.5, shape: 'triangle', gain: 0.14 },
  { at: 1.3, note: 'D4', dur: 0.7, shape: 'triangle', gain: 0.13 },
];

/** Tiny phase-up flourish: quick 3-note ascent, ~0.6s. */
export const STINGER_PHASE_UP: StingerNote[] = [
  { at: 0.0, note: 'C5', dur: 0.14, shape: 'square', duty: 0.25, gain: 0.14 },
  { at: 0.13, note: 'E5', dur: 0.14, shape: 'square', duty: 0.25, gain: 0.14 },
  { at: 0.26, note: 'G5', dur: 0.3, shape: 'square', duty: 0.25, gain: 0.15 },
];
